import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchAPI } from '../utils/api';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Load user and fetch users list on mount
    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            const user = JSON.parse(storedUser);
            setCurrentUser(user);

            // If admin, fetch all users
            if (user.role === 'admin') {
                loadUsers();
            }
        }
        setLoading(false);
    }, []);

    const loadUsers = async () => {
        try {
            const data = await fetchAPI('/auth/get_users.php');
            setUsers(data);
        } catch (err) {
            console.error("Failed to load users", err);
        }
    };

    const register = async (email, password, name, studentType) => {
        try {
            // Future endpoint: /auth/register.php
            const user = await fetchAPI('/auth/register.php', {
                method: 'POST',
                body: JSON.stringify({ email, password, name, student_type: studentType })
            });

            setCurrentUser(user);
            localStorage.setItem('user', JSON.stringify(user));
            return user;
        } catch (error) {
            throw error;
        }
    };

    const login = async (email, password) => {
        try {
            const result = await fetchAPI('/auth/login.php', {
                method: 'POST',
                body: JSON.stringify({ email, password })
            });

            // If result has status 'busy', return the whole result so the component can handle it
            if (result && result.status === 'busy') {
                return result;
            }

            // Normal login success
            setCurrentUser(result);
            localStorage.setItem('user', JSON.stringify(result));
            return result;
        } catch (error) {
            throw error;
        }
    };

    const logout = () => {
        setCurrentUser(null);
        localStorage.clear(); // Clear all local storage
        sessionStorage.clear(); // Clear session storage
        // Force reload to clear any memory cache
        window.location.href = '#/login';
    };

    const changePassword = async (currentPassword, newPassword) => {
        return await fetchAPI('/auth/change_password.php', {
            method: 'POST',
            body: JSON.stringify({ user_id: currentUser.id, current_password: currentPassword, new_password: newPassword })
        });
    };

    const forgotPassword = async (email) => {
        return await fetchAPI('/auth/forgot_password.php', {
            method: 'POST',
            body: JSON.stringify({ email })
        });
    };

    // Update user profile
    const updateUser = async (updatedData) => {
        try {
            const data = await fetchAPI('/auth/update_profile.php', {
                method: 'POST',
                body: JSON.stringify({ ...updatedData, id: currentUser.id })
            });
            setCurrentUser(data);
            localStorage.setItem('user', JSON.stringify(data));
            return data;
        } catch (error) {
            throw error;
        }
    };

    const value = {
        currentUser,
        login,
        register,
        logout,
        updateUser,
        changePassword,
        forgotPassword,
        users,
        loadUsers
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}
